/**
 * Single onRenderClient for all routes so we always reuse the same root on #root.
 * Prevents "createRoot() on a container that has already been passed to createRoot()" when
 * navigating from a non-SRP page (e.g. job detail) back to the SRP via the logo.
 * Progressive SRP: on SRP we do NOT hydrate; we run the island loader. Other routes hydrate as usual.
 */
import type { PageContextClient } from 'vike/types';
import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { getReactRootSentryOptions } from '../utils/react/react-root-sentry-options';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { VikeReactProviderPageContext } from 'vike-react/usePageContext';
import LayoutDefault from '../layouts/LayoutDefault';
import { createStore } from '../redux/createStore';
import { runSrpIslandLoader, unmountSrpIsland } from './index/srp.client';
import { runJobIslandLoader, unmountJobIsland } from './job/job.client';
import {
  runBestCompaniesDimensionIslandLoader,
  unmountBestCompaniesDimensionIsland,
} from './best-companies/@dimension/best-companies-dimension.client';
import { runLocationsIslandLoader, unmountLocationsIsland } from './locations/locations.client';
import { runSalaryIslandLoader, unmountSalaryIsland } from './salary/salary.client';
import { runSalaryRoleIslandLoader, unmountSalaryRoleIsland } from './salary/@role/salary-role.client';
import { isStaticDestination, isServerDataOnlyDestination } from '../utils/routing/is-static-destination';
import { JOBS_LISTING_SEGMENT, JOBS_LISTING_SEGMENTS } from '../utils/constants/url-constants';
import { initStandaloneNavigationProgress } from '../utils/navigation/standalone-navigation-progress';
import { installTranslateSafeDomPatches } from '../utils/browser/install-translate-safe-dom-patches';

export { onRenderClient };

// Runs once per page load, regardless of which route/island ends up mounting below — see the
// module doc comment for why this can't just be the existing React-context NavigationProgress.
if (typeof window !== 'undefined') {
  initStandaloneNavigationProgress();
  // Must run before any hydrateRoot/createRoot call below — see the module doc comment on
  // installTranslateSafeDomPatches for why (protects every route, including ones not otherwise
  // touched here).
  installTranslateSafeDomPatches();
}

/** Reuse root on client-side navigation so we never call hydrateRoot/createRoot twice on #root. */
let vikeRoot: Root | null = null;
let vikeRootContainer: HTMLElement | null = null;

/** Store root in global so that after client-side nav from a page that used a different chunk, we can still reuse. */
const GLOBAL_ROOT_KEY = '__VIKE_ROOT__';
const GLOBAL_CONTAINER_KEY = '__VIKE_ROOT_CONTAINER__';

function getStoredRoot(): { root: Root; container: HTMLElement } | null {
  if (typeof window === 'undefined') return null;
  const root = (window as any)[GLOBAL_ROOT_KEY];
  const container = (window as any)[GLOBAL_CONTAINER_KEY];
  if (root && container && typeof root.render === 'function') return { root, container };
  return null;
}

function setStoredRoot(root: Root, container: HTMLElement): void {
  if (typeof window === 'undefined') return;
  (window as any)[GLOBAL_ROOT_KEY] = root;
  (window as any)[GLOBAL_CONTAINER_KEY] = container;
}

function clearStoredRoot(): void {
  if (typeof window === 'undefined') return;
  delete (window as any)[GLOBAL_ROOT_KEY];
  delete (window as any)[GLOBAL_CONTAINER_KEY];
}

/**
 * vike-react's own onRenderClient would keep document.title in sync on every client-side
 * navigation (its applyHead -> getHeadSetting('title', ...) -> document.title = ...), but that
 * hook is entirely replaced by this file (onRenderClient is not a cumulative Vike hook, so
 * defining our own here drops vike-react's), so nothing was doing this. Every page still defines
 * a +title.ts (or +config.ts title), so we just replicate vike-react's own lookup: read the
 * resolved `title` config off pageContext (a plain value, or a function of pageContext — see
 * vike-react's `title` meta config, which has env: { server: true, client: true } specifically so
 * this works) and apply it. Skipped on hydration/initial load, where the SSR-streamed <title> is
 * already correct — same as vike-react's own `!pageContext.isHydration` gate.
 */
function syncDocumentTitle(pageContext: PageContextClient): void {
  if (typeof document === 'undefined' || !pageContext.isClientSideNavigation) return;
  const configTitle = (pageContext as any).config?.title;
  const title = typeof configTitle === 'function' ? configTitle(pageContext) : configTitle;
  if (title !== undefined) {
    document.title = title || '';
  }
}

function isIndexRoute(pageContext: PageContextClient): boolean {
  // On client, always use window.location.pathname for route detection—pageContext can be stale
  // during navigation (e.g. SRP -> /en/resources), causing us to wrongly return early and not update the page.
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  const segments = String(path).split('/').filter(Boolean);
  if (segments.length === 0) return true;
  const [first, second] = [segments[0], segments[1]];
  if (['en', 'fi', 'sv'].includes(first) && segments.length === 1) return true;
  if (first && JOBS_LISTING_SEGMENT[first as keyof typeof JOBS_LISTING_SEGMENT] === second) return true;
  if (JOBS_LISTING_SEGMENTS.includes(first)) return true;
  return false;
}

function isJobRoute(pageContext: PageContextClient): boolean {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  return /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/)?$/.test(String(path))
    || /^\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/)?$/.test(String(path));
}

const BEST_COMPANIES_DIMENSION_ROUTE_RE =
  /^\/(?:en\/employers|fi\/tyonantajat|sv\/alla-arbetsgivare|employers|tyonantajat|alla-arbetsgivare|en\/best-companies|fi\/parhaat-yritykset|sv\/basta-foretag|best-companies|parhaat-yritykset|basta-foretag)\/[^/]+\/?$/;

function isBestCompaniesDimensionRoute(pageContext: PageContextClient): boolean {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  return BEST_COMPANIES_DIMENSION_ROUTE_RE.test(String(path));
}

const LOCATIONS_HUB_ROUTE_RE = /^\/(?:en\/locations|fi\/sijainnit|sv\/platser|locations|sijainnit|platser)\/?$/;

function isLocationsHubRoute(pageContext: PageContextClient): boolean {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  return LOCATIONS_HUB_ROUTE_RE.test(String(path));
}

const SALARY_HUB_ROUTE_RE = /^\/(?:en\/salary|fi\/palkka|sv\/lon|salary|palkka|lon)\/?$/;

function isSalaryHubRoute(pageContext: PageContextClient): boolean {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  return SALARY_HUB_ROUTE_RE.test(String(path));
}

const SALARY_ROLE_ROUTE_RE = /^\/(?:en\/salary|fi\/palkka|sv\/lon|salary|palkka|lon)\/[^/]+\/?$/;

function isSalaryRoleRoute(pageContext: PageContextClient): boolean {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  return SALARY_ROLE_ROUTE_RE.test(String(path));
}

async function onRenderClient(pageContext: PageContextClient): Promise<void> {
  // Static pages (landing, resource pages) use clientHooks: false — Vike marks them
  // as isHtmlOnly, so the client router treats them as non-SPA-navigable and performs
  // a full browser navigation before ever calling onRenderClient. This block is a
  // belt-and-suspenders fallback for any edge case where Vike still invokes us here.
  // Only reload on a client-side SPA navigation — on initial load the static HTML is
  // already displayed and we must not reload (would loop in dev with Vite HMR).
  if (typeof window !== 'undefined' && isStaticDestination(window.location.pathname)) {
    if (pageContext.isClientSideNavigation) {
      window.location.reload();
    }
    return;
  }
  // Server-data-only pages (roles index, employers A to Z) hydrate normally on a fresh load so
  // the menu and language switcher work; only a soft navigate() into them needs a real reload,
  // because their +data.server.ts never runs on the client.
  if (
    typeof window !== 'undefined' &&
    pageContext.isClientSideNavigation &&
    isServerDataOnlyDestination(window.location.pathname)
  ) {
    window.location.reload();
    return;
  }

  // 404/error page: always do a hard reload so the server returns the static error page HTML.
  // The _error page uses injectScriptsAt: null and StaticPageLayout — it cannot be client-rendered.
  // This handles all invalid URLs including legacy job URL formats like /fi/job/<base64>.
  if (typeof window !== 'undefined' && (pageContext as any).is404 === true) {
    if (pageContext.isClientSideNavigation) {
      window.location.reload();
    }
    return;
  }

  syncDocumentTitle(pageContext);

  const root = typeof document !== 'undefined' ? document.getElementById('root') : null;
  const Page = pageContext.Page;
  const Layout = (pageContext as any).Layout ?? LayoutDefault;
  if (!root || !Page) return;

  // Sync from global in case we're in a new chunk and module-level vikeRoot is null (e.g. nav from non-index to index).
  const stored = getStoredRoot();
  if (stored && stored.container === root) {
    vikeRoot = stored.root;
    vikeRootContainer = stored.container;
  }

  // SRP islands rework Phase 2: #srp-ssr is now the real hydration target (server-rendered
  // SrpApp, see LayoutDefault.tsx) — there's no separate #srp-initial bootstrap script anymore
  // (pageContext.data carries the full view model via passToClient instead).
  const srpSsr = typeof document !== 'undefined' ? document.getElementById('srp-ssr') : null;
  const jobSsr = typeof document !== 'undefined' ? document.getElementById('job-ssr') : null;
  const jobInitial = typeof document !== 'undefined' ? document.getElementById('job-initial') : null;
  const bcdSsr = typeof document !== 'undefined' ? document.getElementById('best-companies-dimension-ssr') : null;
  const bcdInitial =
    typeof document !== 'undefined' ? document.getElementById('best-companies-dimension-initial') : null;
  const locSsr = typeof document !== 'undefined' ? document.getElementById('locations-ssr') : null;
  const locInitial = typeof document !== 'undefined' ? document.getElementById('locations-initial') : null;
  const salSsr = typeof document !== 'undefined' ? document.getElementById('salary-ssr') : null;
  const salInitial =
    typeof document !== 'undefined' ? document.getElementById('salary-index-page-initial') : null;
  const salRoleSsr = typeof document !== 'undefined' ? document.getElementById('salary-role-ssr') : null;
  const salRoleInitial =
    typeof document !== 'undefined' ? document.getElementById('salary-role-page-initial') : null;

  // Job detail: static shell then island swap (like SRP). Do not hydrate; run job island.
  // Fresh document load — the static shell is already fully painted, so defer the real mount to
  // idle time instead of mounting eagerly (same reasoning as the SRP island's mountImmediately: false).
  if (isJobRoute(pageContext) && jobSsr && jobInitial) {
    runJobIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Only run SRP island loader when we're actually on the index route. When navigating from
  // index to another page (e.g. /en/about), the DOM may still have srp-ssr;
  // we must render the new page (with Layout + IntlProvider) instead of returning early.
  if (isIndexRoute(pageContext) && srpSsr) {
    runSrpIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Best-companies dimension: static shell then island swap (like SRP/job). Do not hydrate;
  // run the best-companies-dimension island loader instead.
  if (isBestCompaniesDimensionRoute(pageContext) && bcdSsr && bcdInitial) {
    runBestCompaniesDimensionIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Locations hub: static shell then island swap (like SRP/job/best-companies-dimension). Do not
  // hydrate; run the locations island loader instead.
  if (isLocationsHubRoute(pageContext) && locSsr && locInitial) {
    runLocationsIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Salary hub: static shell then island swap (like the other island routes). Do not hydrate;
  // run the salary island loader instead.
  if (isSalaryHubRoute(pageContext) && salSsr && salInitial) {
    runSalaryIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Salary role page: static shell then island swap (like the other island routes). Do not
  // hydrate; run the salary-role island loader instead.
  if (isSalaryRoleRoute(pageContext) && salRoleSsr && salRoleInitial) {
    runSalaryRoleIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Past this point we're rendering generically (island fast paths above already returned),
  // which means any live SRP/job/best-companies-dimension/locations/salary/salary-role island is
  // about to have its container ripped out from under it by the vikeRoot render/createRoot
  // below. Unmount those island roots synchronously first so their own unmount effects (e.g.
  // MapView's Mapbox cleanup) run while the container is still attached — otherwise e.g. the map
  // is torn out without cleanup and throws when it's eventually unmounted against an
  // already-detached container on a later nav.
  if (srpSsr) unmountSrpIsland();
  if (jobSsr && jobInitial) unmountJobIsland();
  if (bcdSsr && bcdInitial) unmountBestCompaniesDimensionIsland();
  if (locSsr && locInitial) unmountLocationsIsland();
  if (salSsr && salInitial) unmountSalaryIsland();
  if (salRoleSsr && salRoleInitial) unmountSalaryRoleIsland();

  const layoutTree = Layout
    ? React.createElement(Layout, {}, React.createElement(Page))
    : React.createElement(Page);
  // vike-react's usePageContext()/useData() read from this provider; without it they
  // silently return undefined on the client post-hydration (SSR still works, since
  // vike-react's own onRenderHtml wraps with it — this custom onRenderClient does not).
  const providedTree = React.createElement(VikeReactProviderPageContext, { pageContext }, layoutTree);
  const store = createStore(pageContext);
  const tree = React.createElement(Provider, { store, children: providedTree });

  if (vikeRoot && vikeRootContainer === root) {
    vikeRoot.render(tree);
    // Note: no per-route remount branch here anymore. Indexable-page hydration rework: SrpApp,
    // JobDetailPage, the best-companies-dimension page, the locations hub page, and both salary
    // pages all now render directly as part of `tree` above (each via its own LayoutDefault
    // branch), so they're already mounted and interactive the moment vikeRoot.render(tree)
    // commits — there's no separate #<route>-app island left to mount into for any of them.
    return;
  }

  if (vikeRoot && vikeRootContainer) {
    vikeRoot.unmount();
    vikeRoot = null;
    vikeRootContainer = null;
    clearStoredRoot();
  }

  if (isIndexRoute(pageContext)) {
    // Force createRoot (never hydrateRoot) navigating into SRP via client nav: #root still holds
    // the previous route's DOM, which never matches the SRP tree. SRP islands rework Phase 2:
    // <SrpApp> is already part of `tree` (LayoutDefault's SRP branch renders it directly now), so
    // it mounts fully interactive as part of this same render — no separate island-loader step
    // needed here, unlike the old static-shell-then-swap pattern this used to require.
    vikeRoot = createRoot(root, getReactRootSentryOptions());
    vikeRoot.render(tree);
    vikeRootContainer = root;
    setStoredRoot(vikeRoot, root);
    return;
  }

  // When navigating from SRP, job, best-companies-dimension, locations, salary, or salary-role to
  // another page, #root still has that page's HTML (we returned early). Use createRoot to
  // replace the DOM; hydrateRoot would mismatch and can leave the layout broken.
  const comingFromSrp = !!srpSsr;
  const comingFromJob = !!(jobSsr && jobInitial);
  const comingFromBcd = !!(bcdSsr && bcdInitial);
  const comingFromLoc = !!(locSsr && locInitial);
  const comingFromSal = !!(salSsr && salInitial);
  const comingFromSalRole = !!(salRoleSsr && salRoleInitial);
  const comingFromIsland =
    comingFromSrp || comingFromJob || comingFromBcd || comingFromLoc || comingFromSal || comingFromSalRole;
  const rootOpts = getReactRootSentryOptions();
  vikeRoot = comingFromIsland ? createRoot(root, rootOpts) : hydrateRoot(root, tree, rootOpts);
  if (comingFromIsland) {
    vikeRoot.render(tree);
  }
  vikeRootContainer = root;
  setStoredRoot(vikeRoot, root);
}
